var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as keyboardKey from 'keyboard-key';
import * as PropTypes from 'prop-types';
import * as _ from 'lodash';
import { EventStack } from '../../../lib';
import { getNextElement, getFirstTabbable, getLastTabbable, getWindow, focusAsync, HIDDEN_FROM_ACC_TREE, } from './focusUtilities';
import getUnhandledProps from '../../getUnhandledProps';
import getElementType from '../../getElementType';
import * as customPropTypes from '../../customPropTypes';
/** FocusTrapZone is used to trap the focus in any html element placed in body
 *  and hide other elements outside of Focus Trap Zone from accessibility tree.
 *  Pressing tab will circle focus within the inner focusable elements of the FocusTrapZone. */
var FocusTrapZone = /** @class */ (function (_super) {
    __extends(FocusTrapZone, _super);
    function FocusTrapZone() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this._root = { current: null };
        _this._focusSubscription = EventStack.noSubscription;
        _this._clickSubscription = EventStack.noSubscription;
        _this.createRef = function (elem) { return (_this._root.current = ReactDOM.findDOMNode(elem)); };
        _this._findElementAndFocusAsync = function () {
            if (!_this._root.current)
                return;
            var _a = _this.props, focusPreviouslyFocusedInnerElement = _a.focusPreviouslyFocusedInnerElement, firstFocusableSelector = _a.firstFocusableSelector;
            if (focusPreviouslyFocusedInnerElement &&
                _this._previouslyFocusedElementInTrapZone &&
                _this._root.current.contains(_this._previouslyFocusedElementInTrapZone)) {
                // focus on the last item that had focus in the zone before we left the zone
                focusAsync(_this._previouslyFocusedElementInTrapZone);
                return;
            }
            var focusSelector = firstFocusableSelector &&
                (typeof firstFocusableSelector === 'string'
                    ? firstFocusableSelector
                    : firstFocusableSelector());
            var firstFocusableChild = focusSelector
                ? _this._root.current.querySelector("." + focusSelector)
                : getNextElement(_this._root.current, _this._root.current.firstChild, true, false, false, true);
            firstFocusableChild && focusAsync(firstFocusableChild);
        };
        _this._onFocusCapture = function (ev) {
            _this.props.onFocusCapture && _this.props.onFocusCapture(ev);
            if (ev.target !== ev.currentTarget) {
                // every time focus changes within the trap zone, remember the focused element so that
                // it can be restored if focus leaves the pane and returns via keystroke (i.e. via a call to this.focus(true))
                _this._previouslyFocusedElementInTrapZone = ev.target;
            }
        };
        _this._onKeyboardHandler = function (ev) {
            _this.props.onKeyDown && _this.props.onKeyDown(ev);
            if (ev.isDefaultPrevented() ||
                keyboardKey.getCode(ev) !== keyboardKey.Tab ||
                !_this._root.current) {
                return;
            }
            var _firstTabbableChild = getFirstTabbable(_this._root.current, _this._root.current.firstChild, true);
            var _lastTabbableChild = getLastTabbable(_this._root.current, _this._root.current.lastChild, true);
            if (ev.shiftKey && _firstTabbableChild === ev.target) {
                focusAsync(_lastTabbableChild);
                ev.preventDefault();
                ev.stopPropagation();
            }
            else if (!ev.shiftKey && _lastTabbableChild === ev.target) {
                focusAsync(_firstTabbableChild);
                ev.preventDefault();
                ev.stopPropagation();
            }
        };
        _this._forceFocusInTrap = function (ev, triggeredElement) {
            if (FocusTrapZone._focusStack.length &&
                _this === FocusTrapZone._focusStack[FocusTrapZone._focusStack.length - 1]) {
                if (!_this._root.current.contains(triggeredElement)) {
                    _this._findElementAndFocusAsync();
                    ev.preventDefault();
                    ev.stopPropagation();
                }
            }
        };
        _this._handleOutsideFocus = function (ev) {
            var focusedElement = document.activeElement;
            focusedElement && _this._forceFocusInTrap(ev, focusedElement);
        };
        _this._handleOutsideClick = function (ev) {
            var clickedElement = ev.target;
            clickedElement && _this._forceFocusInTrap(ev, clickedElement);
        };
        _this._subscribeToEvents = function () {
            var _a = _this.props, forceFocusInsideTrap = _a.forceFocusInsideTrap, isClickableOutsideFocusTrap = _a.isClickableOutsideFocusTrap;
            if (forceFocusInsideTrap) {
                _this._focusSubscription.unsubscribe();
                _this._focusSubscription = EventStack.subscribe('focus', _this._handleOutsideFocus, {
                    target: _this.windowElement,
                    useCapture: true,
                });
            }
            if (!isClickableOutsideFocusTrap) {
                _this._clickSubscription.unsubscribe();
                _this._clickSubscription = EventStack.subscribe('click', _this._handleOutsideClick, {
                    target: _this.windowElement,
                    useCapture: true,
                });
            }
        };
        _this._unsubscribeFromEvents = function () {
            var _a = _this.props, forceFocusInsideTrap = _a.forceFocusInsideTrap, isClickableOutsideFocusTrap = _a.isClickableOutsideFocusTrap;
            if (forceFocusInsideTrap) {
                _this._focusSubscription.unsubscribe();
            }
            if (!isClickableOutsideFocusTrap) {
                _this._clickSubscription.unsubscribe();
            }
        };
        _this._getPreviouslyFocusedElementOutsideTrapZone = function () {
            var elementToFocusOnDismiss = _this.props.elementToFocusOnDismiss;
            var previouslyFocusedElement = _this._previouslyFocusedElementOutsideTrapZone;
            if (elementToFocusOnDismiss && previouslyFocusedElement !== elementToFocusOnDismiss) {
                previouslyFocusedElement = elementToFocusOnDismiss;
            }
            else if (!previouslyFocusedElement) {
                previouslyFocusedElement = document.activeElement;
            }
            return previouslyFocusedElement;
        };
        _this._hideContentFromAccessibilityTree = function () {
            var bodyChildren = (document.body && document.body.children) || [];
            if (bodyChildren.length && !document.body.contains(_this._root.current)) {
                // In case popup render options will change
                console.warn('Body element does not contain trap zone element. Please, ensure the trap zone element is placed inside body, so it will work properly.');
            }
            // loop through all body's children, except the last one - which is the popup
            for (var index = 0; index < bodyChildren.length - 1; index++) {
                var element = bodyChildren[index];
                if (element.getAttribute('aria-hidden') !== 'true') {
                    element.setAttribute('aria-hidden', 'true');
                    element.setAttribute(HIDDEN_FROM_ACC_TREE, 'true');
                }
            }
        };
        _this._showContentInAccessibilityTree = function () {
            var hiddenElements = document.querySelectorAll("[" + HIDDEN_FROM_ACC_TREE + "=\"true\"]");
            for (var index = 0; index < hiddenElements.length; index++) {
                var element = hiddenElements[index];
                element.removeAttribute('aria-hidden');
                element.removeAttribute(HIDDEN_FROM_ACC_TREE);
            }
        };
        return _this;
    }
    FocusTrapZone.prototype.componentDidMount = function () {
        FocusTrapZone._focusStack.push(this);
        var _a = this.props.disableFirstFocus, disableFirstFocus = _a === void 0 ? false : _a;
        this.windowElement = getWindow(this._root.current);
        this._previouslyFocusedElementOutsideTrapZone = this._getPreviouslyFocusedElementOutsideTrapZone();
        if (!this._root.current.contains(this._previouslyFocusedElementOutsideTrapZone) &&
            !disableFirstFocus) {
            this._findElementAndFocusAsync();
        }
        this._hideContentFromAccessibilityTree();
        this._subscribeToEvents();
    };
    FocusTrapZone.prototype.render = function () {
        var _a = this.props, className = _a.className, ariaLabelledBy = _a.ariaLabelledBy;
        var rest = getUnhandledProps({ handledProps: _.keys(FocusTrapZone.propTypes).slice() }, this.props);
        // TODO: Remove `as` there after the issue will be resolved:
        // https://github.com/Microsoft/TypeScript/issues/28768
        var ElementType = getElementType({ defaultProps: FocusTrapZone.defaultProps }, this.props);
        return (React.createElement(ElementType, __assign({}, rest, { className: className, ref: this.createRef, "aria-labelledby": ariaLabelledBy, onKeyDown: this._onKeyboardHandler, onFocusCapture: this._onFocusCapture }), this.props.children));
    };
    FocusTrapZone.prototype.componentWillUnmount = function () {
        var _this = this;
        var ignoreExternalFocusing = this.props.ignoreExternalFocusing;
        FocusTrapZone._focusStack = FocusTrapZone._focusStack.filter(function (value) {
            return _this !== value;
        });
        var activeElement = document.activeElement;
        if (!ignoreExternalFocusing &&
            this._previouslyFocusedElementOutsideTrapZone &&
            (this._root.current.contains(activeElement) || activeElement === document.body)) {
            focusAsync(this._previouslyFocusedElementOutsideTrapZone);
        }
        this._unsubscribeFromEvents();
        var lastActiveFocusTrap = FocusTrapZone._focusStack.length &&
            FocusTrapZone._focusStack[FocusTrapZone._focusStack.length - 1];
        if (!lastActiveFocusTrap) {
            this._showContentInAccessibilityTree();
        }
        else if (lastActiveFocusTrap._root.current &&
            lastActiveFocusTrap._root.current.hasAttribute(HIDDEN_FROM_ACC_TREE)) {
            lastActiveFocusTrap._root.current.removeAttribute(HIDDEN_FROM_ACC_TREE);
            lastActiveFocusTrap._root.current.removeAttribute('aria-hidden');
        }
    };
    FocusTrapZone._focusStack = [];
    FocusTrapZone.propTypes = {
        as: customPropTypes.as,
        className: PropTypes.string,
        elementToFocusOnDismiss: PropTypes.object,
        ariaLabelledBy: PropTypes.string,
        isClickableOutsideFocusTrap: PropTypes.bool,
        ignoreExternalFocusing: PropTypes.bool,
        forceFocusInsideTrap: PropTypes.bool,
        firstFocusableSelector: PropTypes.string,
        disableFirstFocus: PropTypes.bool,
        focusPreviouslyFocusedInnerElement: PropTypes.bool,
    };
    FocusTrapZone.defaultProps = {
        as: 'div',
        isClickableOutsideFocusTrap: true,
    };
    return FocusTrapZone;
}(React.Component));
export { FocusTrapZone };
